import numbro from 'numbro';

import { Money } from '../Money';
import {
  customAbbreviationFormat,
  deDeLocaleData as numbroDeDeLocaleData,
  enGbLocaleData as numbroEnGbLocale,
  enUsLocaleData as numbroEnUsLocale,
  pseudoLocale as numbroPsuedoLocale,
} from '../numbro';

import { decode } from './decodeFormat';
import { Format } from './formats';

const DEFAULT_MONEY_FORMAT = decode(Format.MONEY);
const DEFAULT_NUMBER_FORMAT = decode(Format.NUMBER);

// We override the default locale data from moment and numbro for en-GB
// Similar overrides may be desired for other locales
numbro.registerLanguage(numbroEnGbLocale);
numbro.registerLanguage(numbroDeDeLocaleData);
numbro.registerLanguage(numbroEnUsLocale);
if (process.env.NODE_ENV !== 'production') {
  // Set up the pseudo locale in numbro and dayJS
  numbro.registerLanguage(numbroPsuedoLocale);
}

const NON_BREAKING_SPACE = String.fromCharCode(160);

numbro.setDefaults({
  mantissa: 0,
});

const formatBaseNumber = (value: number, format: string) => {
  const f = customAbbreviationFormat(format, value);
  return (
    numbro(value)
      .format(f)
      // Workaround for https://github.com/BenjaminVanRyseghem/numbro/issues/581
      .replace(')%', '%)')
  );
};

export const formatMoney = (value: Money, decodedFormat: string | undefined) => {
  // Typescript should prevent us getting here, but make sure, in case people bypass typechecking
  // We don't return a "default" value, as people should choose for their UI what the empty value should be
  // (dash, N/A, blank etc)
  if (value.amount == null) {
    console.warn('Formatting Money with null/undefined amount');
    return null;
  }
  const currencyWithSpace = value.currency ? `${value.currency}${NON_BREAKING_SPACE}` : '';
  return (
    formatBaseNumber(value.amount, decodedFormat || DEFAULT_MONEY_FORMAT)
      .replace(numbro.languageData().currency.symbol, currencyWithSpace)
      // Numbro puts "-" before currency, we want it after
      .replace(`-${currencyWithSpace}`, `${currencyWithSpace}-`)
      // Numbro puts bracket after the currency, we want it around the currency
      // Workaround for https://github.com/BenjaminVanRyseghem/numbro/issues/581
      .replace(`${currencyWithSpace}(`, `(${currencyWithSpace}`)
  );
};

// The x is a mathematical symbol, rather than a unit, so does not get translated
// Strictly we should be using × (https://en.wikipedia.org/wiki/Multiplication_sign)
const formatRatio = (value: number, decodedFormat: string) =>
  `${formatBaseNumber(value, decodedFormat.replace('x', ''))}x`;

const formatArea = (value: number, decodedFormat: string) =>
  `${formatBaseNumber(value, decodedFormat.replace('sq ft', ''))} sq ft`;

export function formatNumber(value: number, decodedFormat: string | undefined) {
  // Prevent formatting plain numbers with money formats (shows the locale-default currency symbol)
  if (decodedFormat?.includes('$')) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(
        `Trying for format plain number ${value} with currency format ${decodedFormat}
        - use Money for formatting money.`
      );
    }
    decodedFormat = decodedFormat.replace('$', '');
  }
  if (decodedFormat?.includes('sq ft')) {
    return formatArea(value, decodedFormat);
  }
  if (decodedFormat?.includes('x')) {
    return formatRatio(value, decodedFormat);
  }
  return formatBaseNumber(value, decodedFormat || DEFAULT_NUMBER_FORMAT);
}
